import {
  Box,
  Button,
  Divider,
  List,
  Paper,
  Typography,
} from "@mui/material";
import accessCheckIcon from "../../../../../../../assets/images/marathon/access-check-circle-icon.svg";
import { useAppSelector } from "shared/lib/hooks/hooks";
import { useTranslation } from "react-i18next";
import { MarathonListItem } from "../ListItem/ListItem";
import dayjs from "dayjs";
import { FC } from "react";

interface IRegisterInfoProps {
  setChangeDistance?: any;
}

export enum PaymentMethodEnum {
  PWT = 0,
  Voucher = 1,
  Money = 2,
}

const RegistrationInfo: FC<IRegisterInfoProps> = ({
  setChangeDistance,
}) => {
  const { t } = useTranslation();
  const marathon = useAppSelector(
    (state) => state.marathons.marathonsById.response.data
  );
  const registrationInfo = useAppSelector(
    (state) => state.marathons.userRegistered.data
  );
  const hasUserInsurance = useAppSelector(
    (state) =>
      state.marathons.marathonsHasUserInsurance.response.data
  );

  const checkPayment = () => {
    const payment = registrationInfo?.payment;
    const paymentType = registrationInfo?.paymentType;

    if (payment === 0) return t("myMarathon.preferentialParticipant");

    if (payment === 1 && (paymentType === undefined || paymentType === null)) {
      return t("myMarathon.paymentType.vaucher");
    }

    if (payment === 1 && paymentType === 1) return t("myMarathon.paymentType.onlain/vaucher");

    if (payment === 1 && paymentType === 0) return t("myMarathon.paymentType.cash/vaucher");

    if (payment === 2 && paymentType === 1) return t("myMarathon.paymentType.onlain");
    if (payment === 2 && paymentType === 0) return t("myMarathon.paymentType.cash");

    return t("myMarathon.paymentType.unknown");
  };

  return (
    <Paper
      sx={{
        borderRadius: "16px",
        p: { xs: 2, sm: 3 },
        m: { xs: "0 0 15px 0" },
        boxShadow: "none",
        width: "100%",
      }}
    >
      <Box
        sx={{
          my: 2,
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontWeight: 600,
          fontSize: { xs: 14, sm: 16 },
          color: "#3e444a",
        }}
      >
        <img src={accessCheckIcon} alt="Image" />
        <Typography>
          {t("myMarathon.alreadyRegistered")}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          pb: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 18, sm: 22 },
            fontWeight: 500,
            color: "#3E444A",
            whiteSpace: "nowrap",
          }}
        >
          {t("myMarathon.participation")}
        </Typography>
        <Divider sx={{ flexGrow: 1 }} />
      </Box>

      <List sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <MarathonListItem
          label={t("myMarathon.eventDate")}
          value={dayjs(registrationInfo?.marathonDate).format(
            "DD.MM.YYYY"
          )}
        />
        <MarathonListItem
          label={t("myMarathon.venue")}
          value={registrationInfo?.place}
        />
        <MarathonListItem
          label={t("myMarathon.distance")}
          value={registrationInfo?.distance}
        />
        <MarathonListItem
          label={t("application.acceptance")}
          value={`${dayjs(
            marathon?.startDateAcceptingApplications
          ).format("DD.MM.YYYY")} – ${dayjs(
            marathon?.endDateAcceptingApplications
          ).format("DD.MM.YYYY")}`}
        />
        <MarathonListItem
          label={t("myMarathon.participantNumbersPrintDate")}
          value={dayjs(
            marathon?.participantNumbersPrintDate
          ).format("DD.MM.YYYY")}
        />
        <MarathonListItem
          label={t("myMarathon.price")}
          value={`${registrationInfo?.price
            ? parseFloat(registrationInfo?.price).toFixed(0)
            : "0"} ${t("myMarathon.som")}`}
        />
      </List>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          py: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 18, sm: 22 },
            fontWeight: 500,
            color: "#3E444A",
            whiteSpace: "nowrap",
          }}
        >
          {t("myMarathon.paymentDetails")}
        </Typography>
        <Divider sx={{ flexGrow: 1 }} />
      </Box>

      <List sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <MarathonListItem
          label={t("myMarathon.paymentAmount")}
          value={`${registrationInfo?.paid
            ? parseFloat(registrationInfo?.paid).toFixed(0)
            : "0"} ${t("myMarathon.som")}`}
        />
        <MarathonListItem
          label={t("myMarathon.transactionDate")}
          value={dayjs(
            registrationInfo?.applicationDate
          ).format("DD.MM.YYYY")}
        />
        <MarathonListItem
          label={t("myMarathon.voucherPayment")}
          value={
            registrationInfo?.payment ===
              PaymentMethodEnum.Voucher
              ? t("myMarathon.yes")
              : t("myMarathon.no")
          }
        />
        <MarathonListItem
          label={t("myMarathon.corporateClientName")}
          value={
            registrationInfo?.corporateClientName ||
            t("myMarathon.no")
          }
        />
        <MarathonListItem
          label={t("myMarathon.paymentMethod")}
          value={checkPayment()}
        />
        <MarathonListItem
          label={t("myMarathon.insurance")}
          value={
            <Typography
              sx={{
                fontWeight: 600,
                color: hasUserInsurance ? "green" : "red",
              }}
            >
              {hasUserInsurance
                ? t("myMarathon.yes")
                : t("myMarathon.no")}
            </Typography>
          }
        />
      </List>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          py: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 18, sm: 22 },
            fontWeight: 500,
            color: "#3E444A",
            whiteSpace: "nowrap",
          }}
        >
          {t("myMarathon.order")}
        </Typography>
        <Divider sx={{ flexGrow: 1 }} />
      </Box>

      <List sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {hasUserInsurance && (
          <MarathonListItem
            label={t("viewMarathon.insurance")}
            value={`${marathon?.insurancePrice} ${t(
              "myMarathon.som"
            )}`}
          />
        )}

        {registrationInfo?.productItems?.length ? (
          registrationInfo.productItems.map((p) => (
            <MarathonListItem
              key={p.id}
              label={`${p.name} × ${p.quantity}`}
              value={`${p.price} ${t("myMarathon.som")}`}
            />
          ))
        ) : (
          <MarathonListItem
            label={t("registerToMarathon.noProducts")}
            value=""
          />
        )}
      </List>
      <Box
        sx={{
          display: "flex",
          justifyContent: { xs: "center", sm: "flex-end" },
          mt: 3,
        }}
      >
        <Button
          variant="contained"
          onClick={() => setChangeDistance(true)}
          sx={{
            px: { xs: 4, sm: 8 },
            py: 1,
            background: "#0090D1",
            borderRadius: "12px",
            width: { xs: "100%", sm: "auto" },
            "&:hover": {
              backgroundColor: "#007BB3",
            },
          }}
        >
          {t("myMarathon.changeDistance")}
        </Button>
      </Box>
    </Paper>
  );
};

export default RegistrationInfo;
